/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { AnalysisResponse, AnalysisType } from './analysis.js';
import type { EcsperClientConfig, RequestOptions } from './config.js';
import { DEFAULT_MAX_RETRIES, DEFAULT_TIMEOUT_MS } from './constants.js';
import { EcsperNetworkError, EcsperTimeoutError } from './errors.js';
import { runRequestInterceptors, runResponseInterceptors } from './interceptors.js';
import type { ActiveReportsResponse, OpenInWordResponse } from './reports.js';
import { buildRequest, generateRequestId, type RequestBody } from './request.js';
import { buildApiError, parseBlobResponse, parseJsonResponse } from './response.js';
import { defaultSleep, isRetryableStatus, nextDelayMs } from './retry.js';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface InternalRequestArgs {
  readonly method: HttpMethod;
  readonly path: string;
  readonly body: RequestBody;
  readonly options: RequestOptions | undefined;
}

/**
 * Typed HTTP client for all Ecsper APIs consumed by the Microsoft 365
 * Office Add-in. Sole transport layer: no other workspace package or app may
 * use raw `fetch`, `axios`, `ky`, or any other HTTP library.
 *
 * Design references:
 * - ECS-DD-MS365-001 v1.0.0 §B2 (api-client capability), §B4 (endpoint shapes
 *   and error format), §B1.2 (technology stack).
 * - CTR-PL-MS-OFFICESSO-001 v1.0.0 §3.1 (mandatory request headers), §6
 *   (error envelope).
 * - CTR-MS365-CONTRACTS-v1.0.0 §"Rate Limiting" and §"Error Contract".
 */
export class EcsperClient {
  private readonly config: EcsperClientConfig;
  private readonly fetchImpl: typeof fetch;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly maxRetries: number;
  private readonly timeoutMs: number;

  public constructor(config: EcsperClientConfig) {
    this.config = config;
    // Office Add-in task pane runs in a sandboxed iframe where native methods
    // like fetch must retain their global object binding. Storing the bare
    // `fetch` reference and calling it as `this.fetchImpl(...)` loses that
    // binding and causes "Illegal invocation" at runtime.
    this.fetchImpl = (config.fetch ?? globalThis.fetch).bind(globalThis);
    this.sleep = config.sleep ?? defaultSleep;
    this.maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  /** JSON `GET` request returning typed response `T`. */
  public get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.requestJson<T>({ method: 'GET', path, body: null, options });
  }

  /** JSON `POST` request returning typed response `T`. */
  public post<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.requestJson<T>({
      method: 'POST',
      path,
      body: { kind: 'json', value: body },
      options,
    });
  }

  /** JSON `PUT` request returning typed response `T`. */
  public put<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.requestJson<T>({
      method: 'PUT',
      path,
      body: { kind: 'json', value: body },
      options,
    });
  }

  /** JSON `PATCH` request returning typed response `T`. */
  public patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.requestJson<T>({
      method: 'PATCH',
      path,
      body: { kind: 'json', value: body },
      options,
    });
  }

  /** JSON `DELETE` request returning typed response `T`. */
  public delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.requestJson<T>({ method: 'DELETE', path, body: null, options });
  }

  /**
   * Multipart `POST` request (for `POST /workspace/reports/{rid}/save` per
   * ECS-DD-MS365-001 §B4.2 which accepts `multipart/form-data`). The
   * `Content-Type` header is intentionally omitted so the platform fetch
   * implementation appends the correct multipart boundary.
   */
  public postMultipart<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
    return this.requestJson<T>({
      method: 'POST',
      path,
      body: { kind: 'multipart', value: formData },
      options,
    });
  }

  /**
   * Binary `GET` returning a `Blob` (used by `GET /workspace/dl/{token}` per
   * ECS-DD-MS365-001 §B4.2 to download a DOCX).
   */
  public getBlob(path: string, options?: RequestOptions): Promise<Blob> {
    return this.requestBinary({ method: 'GET', path, body: null, options });
  }

  /**
   * Fetch all active EW reports for the authenticated user, grouped by matter.
   * ECS-RES-WK-MS365-ACTIVE-REPORTS-CONTRACT-001 / ECS-DD-WK-DOCX-GEN-001 §8.
   */
  public getActiveReports(): Promise<ActiveReportsResponse> {
    return this.get<ActiveReportsResponse>('/api/v1/workspace/reports/active/');
  }

  /**
   * Get a presigned URL to open a report's DOCX in Word.
   * `GET /api/v1/workspace/expert/reports/{reportId}/open-in-word/`.
   */
  public getReportOpenUrl(reportId: string): Promise<OpenInWordResponse> {
    return this.get<OpenInWordResponse>(
      `/api/v1/workspace/expert/reports/${reportId}/open-in-word/`,
    );
  }

  /**
   * Request AI analysis for an expert report (stub until Stage 6 AI Gateway).
   * `POST /api/v1/workspace/expert/reports/{reportId}/analyse`.
   */
  public analyseReport(reportId: string, analysisType: AnalysisType): Promise<AnalysisResponse> {
    return this.post<AnalysisResponse>(`/api/v1/workspace/expert/reports/${reportId}/analyse`, {
      analysis_type: analysisType,
    });
  }

  private async requestJson<T>(args: InternalRequestArgs): Promise<T> {
    const response = await this.dispatch(args);
    return parseJsonResponse<T>(response);
  }

  private async requestBinary(args: InternalRequestArgs): Promise<Blob> {
    const response = await this.dispatch(args);
    return parseBlobResponse(response);
  }

  /**
   * Execute a request with timeout, interceptors, retry, and structured error
   * parsing. Returns the raw `Response` once a non-retryable, non-error
   * outcome is reached; the caller-specific parser then converts to `T` or
   * `Blob`.
   */
  private async dispatch(args: InternalRequestArgs): Promise<Response> {
    const { method, path, body, options } = args;
    const requestId = generateRequestId();
    const requestInterceptors = this.config.requestInterceptors ?? [];
    const responseInterceptors = this.config.responseInterceptors ?? [];
    const skipRetry = options?.skipRetry === true;
    const timeoutMs = options?.timeoutMs ?? this.timeoutMs;
    const maxAttempts = skipRetry ? 1 : this.maxRetries + 1;

    let lastError: unknown = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const controller = new AbortController();
      const timeoutHandle = setTimeout(() => {
        controller.abort();
      }, timeoutMs);
      const externalSignal = options?.signal;
      const externalAbortListener = (): void => {
        controller.abort();
      };
      if (externalSignal !== undefined) {
        if (externalSignal.aborted) {
          controller.abort();
        } else {
          externalSignal.addEventListener('abort', externalAbortListener, { once: true });
        }
      }

      try {
        const built = await buildRequest({
          config: this.config,
          method,
          path,
          body,
          options,
          signal: controller.signal,
          requestId,
        });
        const finalRequest = await runRequestInterceptors(built.request, requestInterceptors);

        let rawResponse: Response;
        try {
          rawResponse = await this.fetchImpl(finalRequest);
        } catch (cause) {
          if (controller.signal.aborted && externalSignal?.aborted !== true) {
            throw new EcsperTimeoutError(timeoutMs, requestId, { cause });
          }
          throw new EcsperNetworkError(
            cause instanceof Error ? cause.message : 'Network request failed',
            requestId,
            { cause },
          );
        }

        const response = await runResponseInterceptors(
          rawResponse,
          finalRequest,
          responseInterceptors,
        );

        if (response.ok) {
          return response;
        }

        if (!skipRetry && isRetryableStatus(response.status) && attempt < maxAttempts - 1) {
          const delay = nextDelayMs({ response, attempt });
          await this.sleep(delay);
          lastError = await buildApiError(response, requestId);
          continue;
        }

        throw await buildApiError(response, requestId);
      } catch (error) {
        if (!skipRetry && error instanceof EcsperNetworkError && attempt < maxAttempts - 1) {
          const delay = nextDelayMs({ response: null, attempt });
          await this.sleep(delay);
          lastError = error;
          continue;
        }
        throw error;
      } finally {
        clearTimeout(timeoutHandle);
        if (externalSignal !== undefined) {
          externalSignal.removeEventListener('abort', externalAbortListener);
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new EcsperNetworkError('Retry budget exhausted', requestId);
  }
}
